/**
 * ============================================
 * USE MEDICOS REMITENTES - Hooks para gestión de médicos remitentes
 * ============================================
 *
 * Hooks de TanStack Query para:
 * - Listar médicos remitentes (useQuery con paginación)
 * - Crear médico remitente (useMutation)
 * - Actualizar médico remitente (useMutation)
 * - Eliminar médico remitente (useMutation)
 * - Activar/Desactivar médico remitente (useMutation)
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  getMedicosRemitentes,
  getMedicoRemitente,
  createMedicoRemitente,
  updateMedicoRemitente,
  deleteMedicoRemitente,
  activateMedicoRemitente,
  deactivateMedicoRemitente,
  buscarMedicosRemitentes,
  type MedicoRemitenteCreateData,
  type MedicoRemitenteUpdateData,
  type MedicoRemitenteFilters,
} from "@/services/medicos-remitentes";

// Re-export functions and constants for useServerDataTable
export {
  getMedicosRemitentes,
  getEspecialidadLabel,
  ESPECIALIDADES_MEDICAS,
} from "@/services/medicos-remitentes";

// Re-export types
export type {
  MedicoRemitente,
  MedicoRemitenteListItem,
  MedicoRemitenteCreateData,
  MedicoRemitenteUpdateData,
  MedicoRemitenteFilters,
} from "@/services/medicos-remitentes";

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Hook para cargar la lista paginada de médicos remitentes
 */
export function useMedicosRemitentes(
  filters?: MedicoRemitenteFilters,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.medicosRemitentes.list(filters || {}),
    queryFn: () => getMedicosRemitentes(filters),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook para cargar un médico remitente por ID
 */
export function useMedicoRemitente(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.medicosRemitentes.detail(id),
    queryFn: () => getMedicoRemitente(id),
    enabled: options?.enabled ?? true,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook para crear un nuevo médico remitente
 */
export function useCreateMedicoRemitente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MedicoRemitenteCreateData) => createMedicoRemitente(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medicosRemitentes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para actualizar un médico remitente
 */
export function useUpdateMedicoRemitente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MedicoRemitenteUpdateData }) =>
      updateMedicoRemitente(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medicosRemitentes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para eliminar un médico remitente (hard delete)
 */
export function useDeleteMedicoRemitente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteMedicoRemitente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medicosRemitentes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para activar un médico remitente
 */
export function useActivateMedicoRemitente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => activateMedicoRemitente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medicosRemitentes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para desactivar un médico remitente (soft delete)
 */
export function useDeactivateMedicoRemitente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deactivateMedicoRemitente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medicosRemitentes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

// =============================================================================
// SEARCH
// =============================================================================

/**
 * Hook para buscar médicos remitentes (autocomplete)
 */
export function useBuscarMedicosRemitentes(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.medicosRemitentes.search(query),
    queryFn: () => buscarMedicosRemitentes(query),
    enabled: (options?.enabled ?? true) && query.length >= 2,
  });
}
